import { RequirePermission } from "@components/common/require-permission";
import { useState } from "react";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { startCase } from "es-toolkit/string";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { BaseMemberProps } from "@custom-types/base-types";
import {
  convertProviderIdToProvider,
  getMemberDisplayName,
} from "@utils/data-display";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { MemberEvents } from "@utils/track-event/track-event-list";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { memberStateSelector } from "@store/member/member-slice";
import { changeMemberAuthMethod } from "@store/member/member-thunks";
import {
  Button,
  Dialog,
  Dropdown,
  NotificationDialog,
  TextField,
  useToast,
  Text,
} from "@flat-ui";

/**
 * Used to change a member's authentication provider.
 * Including SSO providers, email and company SSO.
 */
export function ChangeMemberAuthentication({
  member,
}: BaseMemberProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { handleErrorWithDialog } = useErrorHandler();
  const errorMessage = "Authentication Method Change Error";
  const { openToast } = useToast();
  const dispatch = useAppDispatch();
  const { fetchingMember } = useAppSelector(memberStateSelector);

  // Taken from the member in the store, so that everything reading it, e.g. the reset password
  // button, reflects a change as soon as it is applied.
  const currentProviderId = member.authTypeInfo.identifier;

  const [selectedProvider, setSelectedProvider] =
    useState<FaroCoreApiTypes.AdminAuthMethod>(
      convertProviderIdToProvider(member.authTypeInfo.identifier)
    );

  const [companyId, setCompanyId] = useState<string>("");
  const [shouldShowConfirmationDialog, setShouldShowConfirmationDialog] =
    useState<boolean>(false);
  const [shouldShowProviderNotValid, setShouldShowProviderNotValid] =
    useState<boolean>(false);

  /**
   * Used to determine if the current authentication provider is the same
   * as the authentication provider that the user has selected in the UI.
   * This is to tell the user that the app will reset the authentication
   * provider and not change it to another provider.
   */
  const shouldResetSelectedProvider =
    isSelectedProviderSameAsCurrentProvider(
      selectedProvider,
      currentProviderId
    ) &&
    (selectedProvider !== "company-sso" || !companyId);

  const providerChangeText = shouldResetSelectedProvider ? "reset" : "change";

  const availableProviders = FaroCoreApiConst.allAdminAuthMethods
    .map((provider) => startCase(provider))
    .join(", ");

  const isSelectedProviderCompanySso = selectedProvider === "company-sso";

  async function handleOnDialogConfirm(): Promise<void> {
    if (!member) {
      return;
    }
    trackEvent({
      name: MemberEvents.changeAuthProvider,
      props: {
        previousAuthenticationProvider:
          convertProviderIdToProvider(currentProviderId),
        newAuthenticationProvider: selectedProvider,
      },
    });
    try {
      const updatedMember = await dispatch(
        changeMemberAuthMethod({
          memberId: member.identity,
          provider: selectedProvider,
          companyId,
          shouldReset: shouldResetSelectedProvider,
        })
      ).unwrap();

      // A reset picks the method for us, so take it from the response rather than the selection
      setSelectedProvider(
        convertProviderIdToProvider(updatedMember.authTypeInfo.identifier)
      );
      setCompanyId("");

      openToast({
        title: "Authentication method updated.",
      });
      setIsDialogOpen(false);
    } catch (error) {
      handleErrorWithDialog({
        id: `change-member-login-method-${Date.now().toString()}`,
        title: errorMessage,
        error,
      });
    } finally {
      setShouldShowConfirmationDialog(false);
    }
  }

  function handleChange(provider: string, companyId: string): void {
    setSelectedProvider(provider as FaroCoreApiTypes.AdminAuthMethod);

    if (isNotValidProvider(provider)) {
      setShouldShowProviderNotValid(true);
    }

    if (companyId) {
      setCompanyId("");
    }
  }

  return (
    <>
      <RequirePermission permission="member.changeAuthentication">
        <Button
          onClick={() => {
            trackEvent({
              name: MemberEvents.openChangeAuthProviderDialog,
            });
            setIsDialogOpen(true);
          }}
          variant="secondary"
        >
          Change Authentication Method
        </Button>
      </RequirePermission>

      <Dialog
        open={isDialogOpen}
        title="Change authentication method"
        onCancel={() => {
          setIsDialogOpen(false);
          setSelectedProvider(
            convertProviderIdToProvider(member.authTypeInfo.identifier)
          );
          setCompanyId("");
        }}
        onConfirm={() => setShouldShowConfirmationDialog(true)}
        isConfirmDisabled={
          isNotValidProvider(selectedProvider) ||
          (isSelectedProviderCompanySso &&
            selectedProvider !==
              convertProviderIdToProvider(currentProviderId) &&
            !companyId)
        }
        confirmText={startCase(providerChangeText)}
      >
        <Dropdown
          value={selectedProvider}
          onChange={(ev) => handleChange(ev.target.value, companyId)}
          variant="outlined"
          label="Authenticated Method"
          fullWidth
          options={FaroCoreApiConst.allAdminAuthMethods.map((provider) => ({
            key: provider,
            value: provider,
            label: startCase(provider),
            isDisabled: provider === "service-account",
          }))}
        />
        {isSelectedProviderCompanySso && (
          <TextField
            label="Workspace Id"
            text={companyId}
            onTextChanged={setCompanyId}
            fullWidth
            controlStyle={{ mt: 2 }}
          />
        )}
      </Dialog>

      <NotificationDialog
        open={shouldShowProviderNotValid}
        notificationType="error"
        title="Cannot Change To Selected Authentication Method"
        onCancel={() => setShouldShowProviderNotValid(false)}
        cancelText="Close"
      >
        <Text variant="bodyL">
          <b>{startCase(selectedProvider)}</b> is not a valid authentication
          method. Please choose from: {availableProviders}.
        </Text>
      </NotificationDialog>

      <NotificationDialog
        open={shouldShowConfirmationDialog}
        notificationType="warning"
        title="Change authentication method"
        onCancel={() => setShouldShowConfirmationDialog(false)}
        onConfirm={handleOnDialogConfirm}
        showSpinner={fetchingMember.changingAuthMethod === "pending"}
      >
        <Text variant="bodyL">
          Are you sure you want to {providerChangeText} the authentication
          method for {getMemberDisplayName(member)}
          {shouldResetSelectedProvider && "?"}{" "}
          {!shouldResetSelectedProvider && (
            <>
              to <b>{startCase(selectedProvider)}</b>
              {!companyId && "?"}{" "}
            </>
          )}
          {companyId && (
            <>
              with ID: <b>{companyId}</b>?
            </>
          )}
        </Text>
      </NotificationDialog>
    </>
  );
}

function isNotValidProvider(method: string): boolean {
  return !FaroCoreApiConst.allAdminAuthMethods.includes(
    method as FaroCoreApiTypes.AdminAuthMethod
  );
}

function isSelectedProviderSameAsCurrentProvider(
  selectedProvider: FaroCoreApiTypes.AdminAuthMethod,
  currentProviderId: FaroCoreApiTypes.UserProviderId
): boolean {
  return selectedProvider === convertProviderIdToProvider(currentProviderId);
}
